import { Component, Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AppProvider } from "@/store/AppContext";
import Layout from "@/components/layout/Layout";
import { reportError } from "@/lib/errors";

const RELOAD_KEY = "jb_chunk_reload";
const safe = (loader) => () => loader().then((m) => { sessionStorage.removeItem(RELOAD_KEY); return m; }).catch((e) => {
  if (!sessionStorage.getItem(RELOAD_KEY)) { sessionStorage.setItem(RELOAD_KEY, "1"); window.location.reload(); return new Promise(() => {}); }
  throw e;
});
const lazyPage = (loader) => lazy(safe(loader));

const Home = lazyPage(() => import("@/pages/Home"));
const Category = lazyPage(() => import("@/pages/Category"));
const Section = lazyPage(() => import("@/pages/Category").then((m) => ({ default: m.Section })));
const Product = lazyPage(() => import("@/pages/Product"));
const Search = lazyPage(() => import("@/pages/Search"));
const Cart = lazyPage(() => import("@/pages/Cart"));
const Checkout = lazyPage(() => import("@/pages/Checkout"));
const Orders = lazyPage(() => import("@/pages/Orders"));
const OrderDetail = lazyPage(() => import("@/pages/Orders").then((m) => ({ default: m.OrderDetail })));
const PaymentSuccess = lazyPage(() => import("@/pages/Orders").then((m) => ({ default: m.PaymentSuccess })));
const Login = lazyPage(() => import("@/pages/Login"));
const Account = lazyPage(() => import("@/pages/Account"));
const Addresses = lazyPage(() => import("@/pages/Addresses"));
const Subscriptions = lazyPage(() => import("@/pages/Subscriptions"));
const AP = (name) => lazyPage(() => import("@/pages/AccountPages").then((m) => ({ default: m[name] })));
const ST = (name) => lazyPage(() => import("@/pages/Static").then((m) => ({ default: m[name] })));
const PT = (name) => lazyPage(() => import("@/pages/partner/Partner").then((m) => ({ default: m[name] })));
const AD = (name) => lazyPage(() => import("@/pages/admin/Admin").then((m) => ({ default: m[name] })));
const [Wallet, Offers, Wishlist, Notifications, Refer, Help] = ["Wallet", "Offers", "Wishlist", "Notifications", "Refer", "Help"].map(AP);
const [Legal, About, Contact, AreaPartner, NotFound] = ["Legal", "About", "Contact", "AreaPartner", "NotFound"].map(ST);
const [PartnerLayout, PartnerDashboard, PartnerProducts, PartnerEarnings, PartnerWallet, PartnerCash, PartnerRestock, PartnerSpecialty] = ["PartnerLayout", "PartnerDashboard", "PartnerProducts", "PartnerEarnings", "PartnerWallet", "PartnerCash", "PartnerRestock", "PartnerSpecialty"].map(PT);
const PartnerRoute = lazyPage(() => import("@/pages/partner/PartnerRoute").then((m) => ({ default: m.PartnerRoute })));
const ADMIN = ["AdminLayout", "AdminDashboard", "AdminOrders", "AdminProducts", "AdminCategories", "AdminCustomers", "AdminLocations", "AdminStaff", "AdminPartnerPricing", "AdminPartnerHub", "AdminCoupons", "AdminBanners", "AdminCharges", "AdminNotifications", "AdminSubscriptions", "AdminWithdrawals", "AdminTransactions", "AdminAnalytics", "AdminSupport", "AdminEnquiries", "AdminWaitlist", "AdminSettings", "AdminLegal"];
const A = Object.fromEntries(ADMIN.map((n) => [n, AD(n)]));
const AH = (name) => lazyPage(() => import("@/pages/admin/AdminHome").then((m) => ({ default: m[name] })));
const [AdminHome, AdminInvoiceSettings, AdminCreateOrder] = ["AdminHome", "AdminInvoiceSettings", "AdminCreateOrder"].map(AH);

const ScrollTop = () => { const { pathname } = useLocation(); useEffect(() => { window.scrollTo(0, 0); }, [pathname]); useEffect(() => { prefetchPages(); }, []); return null; };
const PAGES = [() => import("@/pages/Home"), () => import("@/pages/Category"), () => import("@/pages/Product"), () => import("@/pages/Search"), () => import("@/pages/Cart"), () => import("@/pages/Checkout"), () => import("@/pages/Orders"), () => import("@/pages/Login"), () => import("@/pages/Account"), () => import("@/pages/Addresses"), () => import("@/pages/Subscriptions"), () => import("@/pages/AccountPages"), () => import("@/pages/Static")];
const prefetchPages = () => { const run = async () => { for (const p of PAGES) { try { await p(); } catch {} } }; (window.requestIdleCallback || ((f) => setTimeout(f, 1500)))(run); };

const Fallback = <div className="container-x py-24 flex flex-col items-center justify-center gap-4 text-center" data-testid="page-loading"><span className="w-10 h-10 rounded-full border-4 border-[var(--brand-tertiary)] border-t-[var(--brand-primary)] animate-spin" /><p className="text-sm font-semibold text-[var(--on-surface3)]">Loading fresh produce…</p></div>;

class PageBoundary extends Component {
  state = { err: null };
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { reportError(err, "boundary"); }
  componentDidUpdate(prev) { if (prev.path !== this.props.path && this.state.err) this.setState({ err: null }); }
  render() {
    if (!this.state.err) return this.props.children;
    return <div className="container-x py-20 text-center" data-testid="page-error"><h1 className="font-display text-2xl font-semibold">Something went wrong</h1><p className="text-sm text-[var(--on-surface3)] mt-2">A newer version of the site may be available.</p><button onClick={() => window.location.reload()} className="btn-brand mt-5" data-testid="page-error-reload">Reload page</button></div>;
  }
}
const Boundary = ({ children }) => <PageBoundary path={useLocation().pathname}>{children}</PageBoundary>;

export default function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <BrowserRouter>
          <ScrollTop />
          <Boundary>
          <Suspense fallback={Fallback}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/fruits" element={<Category fixedSlug="fruits" />} />
                <Route path="/vegetables" element={<Category fixedSlug="vegetables" />} />
                <Route path="/organic" element={<Category fixedSlug="organic" />} />
                <Route path="/category/subscription" element={<Navigate to="/weekly-basket" replace />} />
                <Route path="/category/:slug" element={<Category />} />
                <Route path="/section/:key" element={<Section />} />
                <Route path="/product/:id" element={<Product />} />
                <Route path="/search" element={<Search />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/order/:id" element={<OrderDetail />} />
                <Route path="/payment-success/:id" element={<PaymentSuccess />} />
                <Route path="/account" element={<Account />} />
                <Route path="/addresses" element={<Addresses />} />
                <Route path="/wallet" element={<Wallet />} />
                <Route path="/offers" element={<Offers />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/refer" element={<Refer />} />
                <Route path="/help" element={<Help />} />
                <Route path="/weekly-basket" element={<Subscriptions />} />
                <Route path="/subscriptions" element={<Navigate to="/weekly-basket" replace />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/area-partner" element={<AreaPartner />} />
                <Route path="/terms" element={<Legal kind="terms" />} />
                <Route path="/privacy" element={<Legal kind="privacy" />} />
                <Route path="/refund-policy" element={<Legal kind="refund" />} />
                <Route path="/legal" element={<Navigate to="/terms" replace />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/login" element={<Login />} />
              <Route path="/partner" element={<PartnerLayout />}>
                <Route index element={<PartnerDashboard />} />
                <Route path="route" element={<PartnerRoute />} />
                <Route path="products" element={<PartnerProducts />} />
                <Route path="b2b-products" element={<PartnerProducts path="/delivery/b2b-products" title="B2B Products" sub="Wholesale catalogue availability and pricing" />} />
                <Route path="restock" element={<PartnerRestock />} />
                <Route path="earnings" element={<PartnerEarnings />} />
                <Route path="wallet" element={<PartnerWallet />} />
                <Route path="cash" element={<PartnerCash />} />
                <Route path="specialty" element={<PartnerSpecialty />} />
              </Route>
              <Route path="/delivery/*" element={<Navigate to="/partner" replace />} />
              <Route path="/admin" element={<A.AdminLayout />}>
                <Route index element={<A.AdminDashboard />} />
                <Route path="orders" element={<A.AdminOrders />} />
                <Route path="create-order" element={<AdminCreateOrder />} />
                <Route path="home" element={<AdminHome />} />
                <Route path="invoice-settings" element={<AdminInvoiceSettings />} />
                <Route path="products" element={<A.AdminProducts />} />
                <Route path="categories" element={<A.AdminCategories />} />
                <Route path="customers" element={<A.AdminCustomers />} />
                <Route path="locations" element={<A.AdminLocations />} />
                <Route path="staff" element={<A.AdminStaff />} />
                <Route path="partner-pricing" element={<A.AdminPartnerPricing />} />
                <Route path="partner-hub" element={<A.AdminPartnerHub />} />
                <Route path="coupons" element={<A.AdminCoupons />} />
                <Route path="banners" element={<A.AdminBanners />} />
                <Route path="charges" element={<A.AdminCharges />} />
                <Route path="notifications" element={<A.AdminNotifications />} />
                <Route path="subscriptions" element={<A.AdminSubscriptions />} />
                <Route path="withdrawals" element={<A.AdminWithdrawals />} />
                <Route path="transactions" element={<A.AdminTransactions />} />
                <Route path="analytics" element={<A.AdminAnalytics />} />
                <Route path="support" element={<A.AdminSupport />} />
                <Route path="enquiries" element={<A.AdminEnquiries />} />
                <Route path="waitlist" element={<A.AdminWaitlist />} />
                <Route path="settings" element={<A.AdminSettings />} />
                <Route path="legal" element={<A.AdminLegal />} />
              </Route>
            </Routes>
          </Suspense>
          </Boundary>
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </AppProvider>
    </HelmetProvider>
  );
}
